import { useState, useEffect } from "react";
import { Calendar, Clock } from "lucide-react";

const LiveDateAndTime = ({ size = "small", className = "" }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer); // Cleanup interval on component unmount
  }, []);

  // Format parts in UK timezone (Europe/London)
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).formatToParts(currentTime);

  const getPart = (type) => parts.find((p) => p.type === type)?.value || "";

  const weekday = getPart("weekday");
  const day = getPart("day");
  const month = getPart("month");
  const year = getPart("year");
  const hour = getPart("hour");
  const minute = getPart("minute");
  const second = getPart("second");
  const dayPeriod = getPart("dayPeriod");

  const isLarge = size === "large";

  if (isLarge) {
    return (
      <div
        className={`inline-flex items-center gap-3 sm:gap-3.5 px-4 py-2 sm:px-5 sm:py-2.5 rounded-2xl bg-color2/70 border border-white/10 backdrop-blur-md shadow-lg shadow-black/25 select-none hover:border-white/20 transition-all duration-300 ${className}`}
      >
        {/* Live Sync Pulse Indicator */}
        <div className="flex items-center gap-1.5" title="Live Terminal Clock (Europe/London)">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]"></span>
          </span>
        </div>

        {/* Date Section */}
        <div className="flex items-center gap-2 text-xs sm:text-sm font-medium">
          <Calendar className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="text-slate-400">{weekday},</span>
          <span className="text-slate-100 font-bold">
            {day} {month} {year}
          </span>
        </div>

        {/* Divider */}
        <div className="h-4.5 w-px bg-white/20" />

        {/* Time Section */}
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-400 shrink-0" />
          <div className="flex items-baseline font-mono">
            <span className="text-base sm:text-lg font-black text-white tracking-wider">
              {hour}:{minute}
            </span>
            <span className="text-xs sm:text-sm font-bold text-amber-400 tracking-wider">
              :{second}
            </span>
          </div>

          {/* AM / PM Badge */}
          <span className="ml-1.5 px-2 py-0.5 rounded-md text-[11px] font-black uppercase tracking-widest bg-amber-400/20 text-amber-300 border border-amber-400/30 shadow-[0_0_8px_rgba(245,158,11,0.25)]">
            {dayPeriod}
          </span>
        </div>
      </div>
    );
  }

  // Small / Compact Variant (Default for Footer / Dashboard)
  return (
    <div
      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-xl bg-color2/70 border border-white/10 backdrop-blur-md shadow-sm shadow-black/20 select-none hover:border-white/20 transition-all duration-300 ${className}`}
    >
      {/* Live Sync Pulse Indicator */}
      <div className="flex items-center gap-1" title="Live Terminal Clock (Europe/London)">
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.8)]"></span>
        </span>
      </div>

      {/* Date Section */}
      <div className="flex items-center gap-1 text-[11px] sm:text-xs font-medium">
        <Calendar className="w-3 h-3 text-amber-400 shrink-0" />
        <span className="text-slate-400">{weekday},</span>
        <span className="text-slate-200 font-semibold">
          {day} {month} {year}
        </span>
      </div>

      {/* Divider */}
      <div className="h-3 w-px bg-white/15" />

      {/* Time Section */}
      <div className="flex items-center gap-1">
        <Clock className="w-3 h-3 text-amber-400 shrink-0" />
        <div className="flex items-baseline font-mono">
          <span className="text-xs sm:text-sm font-extrabold text-white tracking-wider">
            {hour}:{minute}
          </span>
          <span className="text-[10px] sm:text-xs font-bold text-amber-400 tracking-wider">
            :{second}
          </span>
        </div>

        {/* AM / PM Badge */}
        <span className="ml-1 px-1 py-0.2 rounded text-[9px] font-black uppercase tracking-wider bg-amber-400/20 text-amber-300 border border-amber-400/30 shadow-[0_0_6px_rgba(245,158,11,0.2)]">
          {dayPeriod}
        </span>
      </div>
    </div>
  );
};

export default LiveDateAndTime;

